import React from "react";

import { IconEdit } from "~/components/icon";
import { Logger } from "~/tools/logger";

import { useMutation, useQuery, useQueryClient } from "../../queries/react-query";
import {
	mutationBorgDeleteConversation,
	mutationBorgRenameConversation,
	queryBorgConversations,
} from "../queries/borg-conversations";

import { ConversationItem } from "./_history-panel-items";
import { Spinner } from "./_spinner";

const logger = Logger.namespace("HistoryPanel");

/** Native browser confirm — using native dialog for quick destructive-action confirmation. */
// biome-ignore lint/style/noRestrictedGlobals: encapsulated native confirm
// biome-ignore lint/suspicious/noAlert: intentional native confirm for destructive actions
const nativeConfirm = (message: string): boolean => window.confirm(message);

export interface HistoryViewProps {
	activeConversationId: string | undefined;
	onSelectConversation: (id: string) => void;
	onNewConversation: () => void;
}

export const HistoryView: React.FC<HistoryViewProps> = ({
	activeConversationId,
	onSelectConversation,
	onNewConversation,
}) => {
	const queryClient = useQueryClient();
	const deleteConversationMutation = useMutation(mutationBorgDeleteConversation(queryClient));
	const renameConversationMutation = useMutation(mutationBorgRenameConversation(queryClient));
	const conversations = useQuery(queryBorgConversations);
	const conversationItems = conversations.data?.conversations ?? [];

	const handleDeleteConversation = async (id: string) => {
		if (!nativeConfirm("Delete this session? This cannot be undone.")) {
			return;
		}
		try {
			await deleteConversationMutation.mutateAsync(id);
			if (id === activeConversationId) {
				onNewConversation();
			}
		} catch (err) {
			logger.error("Failed to delete session", err);
		}
	};

	const handleRenameConversation = async (id: string, name: string) => {
		try {
			await renameConversationMutation.mutateAsync({ id, displayName: name });
		} catch (err) {
			logger.error("Failed to rename session", err);
		}
	};

	return (
		<div className="flex flex-1 flex-col">
			{/* New session button */}
			<div className="border-grey-300 border-b px-4 py-3">
				<button
					className="flex w-full cursor-pointer items-center justify-center gap-2 rounded-lg border border-grey-300 bg-white px-3 py-2 text-grey-700 text-sm transition-colors hover:bg-grey-100"
					onClick={onNewConversation}
					type="button"
				>
					<IconEdit className="size-4" />
					New session
				</button>
			</div>

			{/* List */}
			<div className="flex-1 overflow-y-auto px-2 py-2">
				{conversations.isLoading ? (
					<div className="flex justify-center py-4">
						<Spinner />
					</div>
				) : conversationItems.length === 0 ? (
					<p className="px-2 py-4 text-center text-grey-500 text-sm">No sessions yet</p>
				) : (
					<div className="flex flex-col gap-1">
						{conversationItems.map((item) => (
							<ConversationItem
								isActive={item.id === activeConversationId}
								item={item}
								key={item.id}
								onDelete={handleDeleteConversation}
								onRename={handleRenameConversation}
								onSelect={onSelectConversation}
							/>
						))}
					</div>
				)}
			</div>
		</div>
	);
};
